import { formatExternalUrl } from './utils.js';
import type { Client } from '../types/index.js';

export const COMMON_COUNTRIES = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'United Arab Emirates',
  'Pakistan',
  'India',
  'Netherlands',
  'Switzerland',
  'Singapore',
  'Spain',
  'Italy',
  'Saudi Arabia',
  'Brazil',
  'Ireland',
  'Sweden',
  'Norway',
  'New Zealand',
  'South Africa',
];

export const COMMON_TIMEZONES = [
  'America/New_York (EST / EDT)',
  'America/Chicago (CST / CDT)',
  'America/Denver (MST / MDT)',
  'America/Los_Angeles (PST / PDT)',
  'Europe/London (GMT / BST)',
  'Europe/Berlin (CET / CEST)',
  'Europe/Paris (CET / CEST)',
  'Asia/Dubai (GST)',
  'Asia/Karachi (PKT)',
  'Asia/Kolkata (IST)',
  'Asia/Singapore (SGT)',
  'Australia/Sydney (AEST / AEDT)',
  'UTC',
];

export const COMMON_CURRENCIES = ['USD', 'EUR', 'GBP', 'PKR', 'CAD', 'AUD', 'AED', 'SGD', 'INR'];

export function getCountryFlag(country?: string | null): string {
  if (!country) return '🌐';
  const c = country.trim().toLowerCase();
  const map: Record<string, string> = {
    'united states': '🇺🇸', 'us': '🇺🇸', 'usa': '🇺🇸', 'united states of america': '🇺🇸',
    'united kingdom': '🇬🇧', 'uk': '🇬🇧', 'great britain': '🇬🇧', 'england': '🇬🇧',
    'canada': '🇨🇦', 'ca': '🇨🇦',
    'germany': '🇩🇪', 'de': '🇩🇪',
    'france': '🇫🇷', 'fr': '🇫🇷',
    'australia': '🇦🇺', 'au': '🇦🇺',
    'pakistan': '🇵🇰', 'pk': '🇵🇰',
    'india': '🇮🇳', 'in': '🇮🇳',
    'united arab emirates': '🇦🇪', 'uae': '🇦🇪', 'ae': '🇦🇪',
    'saudi arabia': '🇸🇦', 'sa': '🇸🇦',
    'netherlands': '🇳🇱', 'nl': '🇳🇱',
    'switzerland': '🇨🇭', 'ch': '🇨🇭',
    'singapore': '🇸🇬', 'sg': '🇸🇬',
    'spain': '🇪🇸', 'es': '🇪🇸',
    'italy': '🇮🇹', 'it': '🇮🇹',
    'brazil': '🇧🇷', 'br': '🇧🇷',
    'turkey': '🇹🇷', 'tr': '🇹🇷',
    'ireland': '🇮🇪', 'ie': '🇮🇪',
    'sweden': '🇸🇪', 'se': '🇸🇪',
    'norway': '🇳🇴', 'no': '🇳🇴',
    'denmark': '🇩🇰', 'dk': '🇩🇰',
    'south africa': '🇿🇦', 'za': '🇿🇦',
    'new zealand': '🇳🇿', 'nz': '🇳🇿',
    'malaysia': '🇲🇾', 'my': '🇲🇾',
    'bangladesh': '🇧🇩', 'bd': '🇧🇩',
    'china': '🇨🇳', 'cn': '🇨🇳',
    'japan': '🇯🇵', 'jp': '🇯🇵',
    'philippines': '🇵🇭', 'ph': '🇵🇭',
    'mexico': '🇲🇽', 'mx': '🇲🇽',
    'indonesia': '🇮🇩', 'id': '🇮🇩',
    'nigeria': '🇳🇬', 'ng': '🇳🇬',
    'egypt': '🇪🇬', 'eg': '🇪🇬',
  };
  if (map[c]) return map[c];
  // Convert any 2-letter ISO country code into regional indicator symbols (flag emoji)
  if (c.length === 2 && /^[a-z]{2}$/i.test(c)) {
    const codePoints = [...c.toUpperCase()].map((ch) => 127397 + ch.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }
  return '🌐';
}

export function cleanPhoneForWhatsApp(num?: string | null): string | null {
  if (!num) return null;
  // Strip international domestic trunk prefix in parentheses, e.g. (0) in +44 (0) 20 ...
  const normalized = num.replace(/\(\s*0\s*\)/g, '');
  let digits = normalized.replace(/[^0-9]/g, '');
  // Strip leading international exit prefix 00 (e.g. 0044 -> 44)
  digits = digits.replace(/^00/, '');
  // If local Pakistani mobile format: 03xx-xxxxxxx (11 digits starting with 03) -> map to 923xxxxxxxxx
  if (/^03\d{9}$/.test(digits)) {
    digits = '92' + digits.slice(1);
  }
  return digits.length >= 7 ? digits : null;
}

export function cleanPhoneForTel(num?: string | null): string | null {
  if (!num) return null;
  const normalized = num.replace(/\(\s*0\s*\)/g, '');
  const sanitized = normalized.replace(/[^0-9+]/g, '');
  return sanitized.length >= 5 ? sanitized : null;
}

export function resolveClientPlatformUrl(client?: Partial<Client> | null): string | undefined {
  if (!client) return undefined;
  if (client.platform_url && formatExternalUrl(client.platform_url)) {
    return formatExternalUrl(client.platform_url);
  }
  const platform = (client.platform || '').toLowerCase();
  const username = (client.platform_username || '').trim().replace(/^@/, '');
  if (username) {
    if (platform === 'fiverr') {
      return `https://www.fiverr.com/${username}`;
    }
  }
  return undefined;
}
